import React, { useEffect, useState } from "react";
import axios from "axios";
import { Routes, Route, Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight,
  Sparkles,
  Search,
  Bot,
  Globe2,
  Rocket,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Copy,
  Check,
  Zap,
  TrendingUp,
  Languages,
  ShieldCheck,
  Star,
  MoveRight,
  Plus,
  Minus,
  Brain,
  Target,
  Layers,
} from "lucide-react";
import { Toaster, toast } from "sonner";
import "@/App.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const AFFILIATE_URL =
  "https://trysoro.com/?linkId=lp_335812&sourceId=geoff-clark&tenantId=soro";

const KEYWORDS = [
  "AI SEO software for solo founders",
  "ChatGPT optimization tool",
  "How to rank on AI search engines",
  "Generative Engine Optimization platform",
  "Automated SEO keyword clustering",
  "Rank higher on Google and ChatGPT",
  "AI content optimization software",
  "Small business SEO automation",
  "Best AEO tool for startups",
  "DIY SEO tools that work",
  "Affordable SEO agency alternative",
  "Optimize content for AI bots",
  "Semantic search ranking tool",
  "Local GEO optimization software",
  "AI-driven content strategy",
  "Increase organic traffic daily",
  "Grow your presence online daily",
  "Automate blog post SEO",
  "Search engine dominance 2026",
  "Next-gen AI marketing platform",
  "Content marketing ROI tool",
  "Programmatic SEO for beginners",
  "AI chatbot discovery optimization",
  "Data-driven keyword research",
  "SEO readiness checker tool",
];

const FAQS = [
  {
    q: "What is Soro and how does it grow my online presence?",
    a: "Soro is an AI SEO autopilot that researches keywords your buyers actually search, writes high-quality articles in your brand voice, and publishes them daily to your website — fully optimized for both Google search and AI engines like ChatGPT.",
  },
  {
    q: "How is this different from hiring an SEO agency?",
    a: "Agencies charge $3k–$10k/month, take weeks to ramp up, and ghost you between deliverables. Soro runs 24/7, publishes every day, and costs a fraction of an agency retainer with zero meetings.",
  },
  {
    q: "Will the content sound like a robot wrote it?",
    a: "No. Soro learns your tone, audience and niche from your site, then writes in your voice. You can edit anything before publishing — but most users hit publish as-is.",
  },
  {
    q: "Does it work with WordPress, Shopify and Webflow?",
    a: "Yes — Soro integrates natively with WordPress, Shopify, Webflow, Wix, Notion, HubSpot, Ghost, Next.js, and exports to anywhere via webhook.",
  },
  {
    q: "What about ranking on ChatGPT and AI Overviews?",
    a: "Every article ships with FAQ schema, HowTo schema, question-style headings and clean structured data — exactly what AI engines pull when answering users.",
  },
  {
    q: "How fast do I see results?",
    a: "Most customers see their first impressions in 2–4 weeks and meaningful traffic in 6–10 weeks. Compounding kicks in around month 3.",
  },
  {
    q: "Can I cancel anytime?",
    a: "Yes. No contracts, no minimum commitments. You own all the content you generate.",
  },
];

// ----------------- Analytics -----------------
const trackCta = (location) => {
  try {
    if (typeof window === "undefined") return;
    // GA4 via gtag (loaded from index.html)
    if (typeof window.gtag === "function") {
      window.gtag("event", "affiliate_cta_click", {
        cta_location: location,
        cta_destination: AFFILIATE_URL,
      });
    }
    // Also push to dataLayer in case GTM is later layered on
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "affiliate_cta_click",
      cta_location: location,
      cta_destination: AFFILIATE_URL,
    });
    // PostHog (already loaded in index.html)
    if (window.posthog && typeof window.posthog.capture === "function") {
      window.posthog.capture("affiliate_cta_click", {
        location,
        destination: AFFILIATE_URL,
      });
    }
  } catch {
    /* never block navigation on analytics */
  }
};

// ----------------- Reusable bits -----------------
const cta = (testId, label = "Start ranking with Soro", location = "generic") => (
  <a
    data-testid={testId}
    href={AFFILIATE_URL}
    target="_blank"
    rel="sponsored noopener noreferrer"
    onClick={() => trackCta(location)}
    className="group inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#FF512F] via-[#FF6F2C] to-[#F09819] px-7 py-3.5 text-sm font-semibold uppercase tracking-wider text-white shadow-[0_10px_40px_-10px_rgba(255,81,47,0.7)] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_20px_60px_-10px_rgba(255,81,47,0.9)]"
  >
    {label}
    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
  </a>
);

const Eyebrow = ({ children, testId }) => (
  <div
    data-testid={testId}
    className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-4 py-1.5 text-[10px] font-bold uppercase tracking-[0.25em] text-[#F09819]"
  >
    <Sparkles className="h-3 w-3" />
    {children}
  </div>
);

// ----------------- Floating gradient blobs -----------------
const Blobs = () => (
  <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
    <div className="absolute -left-32 -top-32 h-[520px] w-[520px] rounded-full bg-[#FF512F] opacity-30 blur-[140px] animate-float-slow" />
    <div className="absolute right-[-10%] top-[20%] h-[460px] w-[460px] rounded-full bg-[#F09819] opacity-25 blur-[140px] animate-float-slow [animation-delay:-6s]" />
    <div className="absolute left-[20%] bottom-[-10%] h-[420px] w-[420px] rounded-full bg-[#00F2FE] opacity-20 blur-[140px] animate-float-slow [animation-delay:-10s]" />
  </div>
);

// ----------------- Header -----------------
const Header = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const onHome = location.pathname === "/";

  // When on /, anchor links scroll. From any other route, navigate home and let the hash trigger scroll.
  const sectionHref = (id) => (onHome ? `#${id}` : `/#${id}`);
  const goToSection = (e, id) => {
    if (onHome) return; // browser handles in-page anchor
    e.preventDefault();
    navigate(`/#${id}`);
  };

  return (
    <header
      data-testid="site-header"
      className="fixed inset-x-0 top-0 z-50 border-b border-white/5 bg-[#050508]/70 backdrop-blur-xl"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
        <Link to="/" className="flex items-center gap-2.5" data-testid="header-logo">
          <div className="relative h-9 w-9 rounded-xl bg-gradient-to-br from-[#FF512F] to-[#F09819] shadow-[0_0_24px_-4px_rgba(255,81,47,0.7)]">
            <div className="absolute inset-[2px] rounded-[10px] bg-[#0F0F16] grid place-items-center">
              <TrendingUp className="h-4 w-4 text-[#F09819]" />
            </div>
          </div>
          <div className="hidden sm:block leading-tight">
            <div className="font-display text-sm font-extrabold tracking-tight">
              Grow Your Presence
            </div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-white/40">
              Online Daily
            </div>
          </div>
        </Link>
        <nav className="hidden items-center gap-8 text-sm text-white/60 md:flex">
          <a
            href={sectionHref("how")}
            onClick={(e) => goToSection(e, "how")}
            className="hover:text-white transition-colors"
            data-testid="nav-how"
          >
            How it works
          </a>
          <a
            href={sectionHref("checker")}
            onClick={(e) => goToSection(e, "checker")}
            className="hover:text-white transition-colors"
            data-testid="nav-checker"
          >
            Free checker
          </a>
          <Link
            to="/blog"
            className={`transition-colors ${
              location.pathname.startsWith("/blog") ? "text-white" : "hover:text-white"
            }`}
            data-testid="nav-blog"
          >
            Blog
          </Link>
          <a
            href={sectionHref("faq")}
            onClick={(e) => goToSection(e, "faq")}
            className="hover:text-white transition-colors"
            data-testid="nav-faq"
          >
            FAQ
          </a>
        </nav>
        {cta("header-cta-button", "Try Soro", "header")}
      </div>
    </header>
  );
};

// ----------------- Hero -----------------
const Hero = () => (
  <section id="top" className="relative overflow-hidden pt-32 pb-24 lg:pt-44 lg:pb-32">
    <Blobs />
    <div className="absolute inset-0 -z-10 bg-grain opacity-40" />

    <div className="mx-auto max-w-7xl px-6">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
        className="mx-auto max-w-4xl text-center"
      >
        <Eyebrow testId="hero-eyebrow">SEO · AEO · GEO autopilot</Eyebrow>

        <h1
          data-testid="hero-headline"
          className="mt-6 font-display text-4xl font-black leading-[0.95] tracking-tighter sm:text-6xl lg:text-7xl"
        >
          Get found on{" "}
          <span className="text-gradient-flame">Google</span>
          <span className="text-white/40"> &amp; </span>
          <span className="text-gradient-flame">ChatGPT</span>
          <br className="hidden sm:block" />
          <span className="text-white/85"> while you sleep.</span>
        </h1>

        <p
          data-testid="hero-subhead"
          className="mx-auto mt-7 max-w-2xl text-base text-white/65 sm:text-lg"
        >
          Soro is the AI SEO autopilot for founders who refuse to pay $5k/month to an agency.
          It researches keywords, writes articles in your voice, and publishes daily — so customers
          find <em>you</em> instead of your competitors.
        </p>

        <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
          {cta("hero-cta-button", "Start Growing Today", "hero")}
          <a
            href="#checker"
            data-testid="hero-secondary-cta"
            className="inline-flex items-center gap-2 rounded-full border border-white/15 px-6 py-3 text-sm font-medium text-white/80 transition-colors hover:bg-white/5"
          >
            Run free SEO/AEO/GEO scan
            <MoveRight className="h-4 w-4" />
          </a>
        </div>

        <div className="mt-12 flex flex-wrap items-center justify-center gap-x-8 gap-y-4 text-sm text-white/60">
          <div className="flex items-center gap-2">
            <div className="flex">
              {[0, 1, 2, 3, 4].map((i) => (
                <Star key={i} className="h-4 w-4 fill-[#F09819] text-[#F09819]" />
              ))}
            </div>
            <span className="font-semibold text-white">4.9/5</span>
            <span>· 1,783 verified customers</span>
          </div>
          <div className="hidden h-4 w-px bg-white/10 sm:block" />
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-[#00F2FE]" />
            No contracts · cancel anytime
          </div>
        </div>
      </motion.div>

      {/* Floating mockup card */}
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, delay: 0.2 }}
        className="relative mx-auto mt-20 max-w-5xl"
      >
        <div className="absolute -inset-x-10 -inset-y-6 -z-10 rounded-[2.5rem] bg-gradient-to-r from-[#FF512F]/30 via-[#F09819]/20 to-[#00F2FE]/30 blur-3xl" />
        <div className="glass-strong rounded-3xl p-2 shadow-2xl">
          <div className="rounded-[1.25rem] border border-white/5 bg-[#0A0A12] p-6 sm:p-10">
            <div className="grid grid-cols-2 gap-6 sm:grid-cols-4">
              {[
                { k: "Articles published", v: 217410, s: "+1,240 today" },
                { k: "Impressions", v: 1620000000, s: "Google + AI" },
                { k: "Clicks driven", v: 30000000, s: "Organic traffic" },
                { k: "Avg position", v: 8.4, s: "Top of page 1" },
              ].map((m, i) => (
                <div key={i} className="border-l-2 border-[#FF512F]/40 pl-4">
                  <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/40">
                    {m.k}
                  </div>
                  <div className="mt-2 font-display text-2xl font-extrabold sm:text-3xl">
                    {m.v >= 1000000000
                      ? `${(m.v / 1000000000).toFixed(1)}B+`
                      : m.v >= 1000000
                      ? `${(m.v / 1000000).toFixed(0)}M+`
                      : m.v >= 1000
                      ? `${(m.v / 1000).toFixed(0)}k+`
                      : m.v}
                  </div>
                  <div className="mt-1 text-xs text-[#00F2FE]">{m.s}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  </section>
);

// ----------------- How It Works -----------------
const HowItWorks = () => {
  const steps = [
    {
      n: "01",
      icon: <Globe2 className="h-5 w-5" />,
      t: "Plug in your site",
      d: "Paste your URL. Soro crawls your pages, learns your tone, niche, and ideal customer in minutes.",
    },
    {
      n: "02",
      icon: <Brain className="h-5 w-5" />,
      t: "AI builds your strategy",
      d: "It finds the exact keywords buyers (not browsers) search — across Google, ChatGPT and Perplexity.",
    },
    {
      n: "03",
      icon: <Rocket className="h-5 w-5" />,
      t: "Publishes on autopilot",
      d: "Daily articles ship straight to WordPress, Shopify, Webflow or any CMS. You touch nothing.",
    },
  ];
  return (
    <section id="how" className="relative py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-6">
        <div className="max-w-3xl">
          <Eyebrow testId="how-eyebrow">How it works</Eyebrow>
          <h2 className="mt-5 font-display text-3xl font-extrabold tracking-tight sm:text-5xl">
            Three steps. <span className="text-gradient-flame">Zero busywork.</span>
          </h2>
          <p className="mt-4 text-white/60">
            Set it up once. Soro handles keyword research, writing, on-page SEO, schemas and publishing
            forever. You focus on shipping product.
          </p>
        </div>

        <div className="mt-14 grid gap-6 md:grid-cols-3">
          {steps.map((s, i) => (
            <motion.div
              key={s.n}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.55, delay: i * 0.08 }}
              data-testid={`how-step-${i + 1}`}
              className="glass group relative overflow-hidden rounded-3xl p-8 transition-all duration-300 hover:-translate-y-1 hover:border-white/20"
            >
              <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-gradient-to-br from-[#FF512F]/20 to-transparent blur-2xl transition-opacity group-hover:opacity-100" />
              <div className="flex items-center gap-3">
                <div className="grid h-10 w-10 place-items-center rounded-xl bg-gradient-to-br from-[#FF512F] to-[#F09819] text-white shadow-lg">
                  {s.icon}
                </div>
                <span className="font-display text-3xl font-black text-white/15">{s.n}</span>
              </div>
              <h3 className="mt-6 font-display text-xl font-bold">{s.t}</h3>
              <p className="mt-3 text-sm leading-relaxed text-white/60">{s.d}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ----------------- Features -----------------
const Features = () => {
  const items = [
    {
      icon: <Target className="h-5 w-5" />,
      t: "Finds buyer-intent keywords",
      d: "Soro mines Google, ChatGPT, Reddit and YouTube to surface phrases your customers literally type before buying.",
    },
    {
      icon: <Bot className="h-5 w-5" />,
      t: "Writes in your brand voice",
      d: "Trains on your existing pages so every article reads like a human on your team wrote it.",
    },
    {
      icon: <Layers className="h-5 w-5" />,
      t: "Built for AI engines",
      d: "Every post ships with FAQ schema, HowTo schema, semantic headings — the stuff ChatGPT actually quotes.",
    },
    {
      icon: <Languages className="h-5 w-5" />,
      t: "50+ languages, one click",
      d: "Spin up localized content for global markets without hiring translators.",
    },
    {
      icon: <Zap className="h-5 w-5" />,
      t: "Daily auto-publish",
      d: "WordPress, Shopify, Webflow, Wix, Notion, HubSpot, Ghost, Next.js — or any CMS via webhook.",
    },
    {
      icon: <Search className="h-5 w-5" />,
      t: "Tracks every rank, every day",
      d: "See clicks, impressions, average position and CTR in one dashboard. Compounding traffic, visualized.",
    },
  ];

  return (
    <section className="relative py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-6">
        <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
          <div className="max-w-2xl">
            <Eyebrow>What Soro does for you</Eyebrow>
            <h2 className="mt-5 font-display text-3xl font-extrabold tracking-tight sm:text-5xl">
              An entire <span className="text-gradient-flame">SEO team</span> in one app.
            </h2>
          </div>
          <p className="max-w-md text-white/55">
            Stop stitching together 7 tools. Soro replaces your keyword researcher, writer, editor,
            SEO consultant and CMS publisher — for less than one freelancer.
          </p>
        </div>

        <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((f, i) => (
            <motion.div
              key={f.t}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.45, delay: i * 0.05 }}
              data-testid={`feature-card-${i}`}
              className="glass rounded-2xl p-7 transition-all duration-300 hover:-translate-y-1 hover:border-white/20"
            >
              <div className="inline-grid h-11 w-11 place-items-center rounded-xl bg-white/5 text-[#F09819] ring-1 ring-white/10">
                {f.icon}
              </div>
              <h3 className="mt-5 font-display text-lg font-bold">{f.t}</h3>
              <p className="mt-2 text-sm leading-relaxed text-white/60">{f.d}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ----------------- Readiness Checker -----------------
const ScoreRing = ({ score, color, label, testId }) => {
  const radius = 56;
  const circ = 2 * Math.PI * radius;
  const offset = circ - (score / 100) * circ;
  return (
    <div className="flex flex-col items-center" data-testid={testId}>
      <div className="relative h-36 w-36">
        <svg viewBox="0 0 140 140" className="h-full w-full -rotate-90">
          <circle cx="70" cy="70" r={radius} stroke="rgba(255,255,255,0.08)" strokeWidth="10" fill="none" />
          <motion.circle
            cx="70"
            cy="70"
            r={radius}
            stroke={color}
            strokeWidth="10"
            strokeLinecap="round"
            fill="none"
            initial={{ strokeDashoffset: circ }}
            animate={{ strokeDashoffset: offset }}
            transition={{ duration: 1.2, ease: "easeOut" }}
            style={{ strokeDasharray: circ, filter: `drop-shadow(0 0 10px ${color})` }}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <div className="font-display text-3xl font-extrabold">{score}</div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-white/40">/ 100</div>
        </div>
      </div>
      <div className="mt-3 font-display text-sm font-bold uppercase tracking-[0.2em]" style={{ color }}>
        {label}
      </div>
    </div>
  );
};

const ItemIcon = ({ status }) => {
  if (status === "good") return <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-400" />;
  if (status === "warn") return <AlertTriangle className="h-4 w-4 shrink-0 text-[#F09819]" />;
  return <XCircle className="h-4 w-4 shrink-0 text-rose-400" />;
};

const Checker = () => {
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("seo");

  const runCheck = async (e) => {
    e?.preventDefault?.();
    if (!url.trim()) {
      setError("Enter a URL to scan");
      return;
    }
    setError("");
    setLoading(true);
    setResult(null);
    try {
      const { data } = await axios.post(`${API}/check`, { url });
      setResult(data);
      setTab("seo");
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not scan that site. Try another URL.");
    } finally {
      setLoading(false);
    }
  };

  const current = result ? result[tab] : null;

  return (
    <section id="checker" className="relative py-24 sm:py-32">
      <div className="mx-auto max-w-5xl px-6">
        <div className="text-center">
          <Eyebrow testId="checker-eyebrow">Free tool · No signup</Eyebrow>
          <h2 className="mt-5 font-display text-3xl font-extrabold tracking-tight sm:text-5xl">
            SEO · AEO · GEO
            <br />
            <span className="text-gradient-flame">Readiness Checker</span>
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-white/60">
            Paste any URL. We scan it for classic SEO, Answer Engine Optimization (ChatGPT readiness)
            and Generative Engine Optimization signals — and tell you exactly what to fix.
          </p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="glass-strong relative mt-12 overflow-hidden rounded-3xl p-1.5 ring-flame"
        >
          <div className="rounded-[1.4rem] bg-[#070710] p-6 sm:p-10">
            <form onSubmit={runCheck} className="flex flex-col gap-3 sm:flex-row">
              <div className="relative flex-1">
                <Globe2 className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-white/30" />
                <input
                  data-testid="checker-url-input"
                  type="text"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="yourwebsite.com"
                  className="w-full rounded-2xl border border-white/10 bg-black/40 py-4 pl-12 pr-4 text-base text-white placeholder-white/30 outline-none transition-all focus:border-[#F09819] focus:ring-1 focus:ring-[#F09819]"
                />
              </div>
              <button
                data-testid="checker-submit-button"
                type="submit"
                disabled={loading}
                className="group inline-flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-[#FF512F] to-[#F09819] px-7 py-4 text-sm font-semibold uppercase tracking-wider text-white shadow-[0_10px_40px_-10px_rgba(255,81,47,0.7)] transition-all hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-70"
              >
                {loading ? (
                  <>
                    <motion.span
                      className="h-4 w-4 rounded-full border-2 border-white/40 border-t-white"
                      animate={{ rotate: 360 }}
                      transition={{ duration: 0.8, repeat: Infinity, ease: "linear" }}
                    />
                    Scanning…
                  </>
                ) : (
                  <>
                    Scan my site
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </>
                )}
              </button>
            </form>

            {error && (
              <div data-testid="checker-error" className="mt-4 rounded-xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-200">
                {error}
              </div>
            )}

            <AnimatePresence mode="wait">
              {loading && !result && (
                <motion.div
                  key="loading"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="mt-10 grid gap-4 sm:grid-cols-3"
                >
                  {["Crawling page…", "Parsing schemas…", "Scoring AI readiness…"].map((s, i) => (
                    <div key={s} className="glass rounded-2xl p-5 text-sm text-white/70">
                      <motion.div
                        animate={{ opacity: [0.3, 1, 0.3] }}
                        transition={{ duration: 1.4, repeat: Infinity, delay: i * 0.3 }}
                        className="flex items-center gap-2"
                      >
                        <Zap className="h-4 w-4 text-[#F09819]" />
                        {s}
                      </motion.div>
                    </div>
                  ))}
                </motion.div>
              )}

              {result && (
                <motion.div
                  key="result"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                  data-testid="checker-result"
                  className="mt-10"
                >
                  <div className="grid grid-cols-3 items-end gap-4 sm:gap-8">
                    <ScoreRing score={result.seo.score} color="#00F2FE" label="SEO" testId="ring-seo" />
                    <ScoreRing score={result.aeo.score} color="#FF512F" label="AEO" testId="ring-aeo" />
                    <ScoreRing score={result.geo.score} color="#F09819" label="GEO" testId="ring-geo" />
                  </div>

                  <div className="mt-8 text-center">
                    <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-4 py-1.5 text-xs text-white/70">
                      Overall readiness: <span className="font-bold text-white">{result.overall_score}/100</span>
                    </div>
                  </div>

                  <div className="mt-8 flex flex-wrap gap-2 border-b border-white/5 pb-1">
                    {[
                      { k: "seo", label: "Classic SEO", color: "#00F2FE" },
                      { k: "aeo", label: "AEO (ChatGPT)", color: "#FF512F" },
                      { k: "geo", label: "GEO (Generative)", color: "#F09819" },
                    ].map((t) => (
                      <button
                        key={t.k}
                        data-testid={`checker-tab-${t.k}`}
                        onClick={() => setTab(t.k)}
                        className={`relative rounded-t-xl px-4 py-2 text-sm font-semibold transition-colors ${
                          tab === t.k ? "text-white" : "text-white/40 hover:text-white/70"
                        }`}
                      >
                        {t.label}
                        {tab === t.k && (
                          <motion.span
                            layoutId="tab-underline"
                            className="absolute inset-x-2 -bottom-0.5 h-0.5 rounded-full"
                            style={{ background: t.color }}
                          />
                        )}
                      </button>
                    ))}
                  </div>

                  <p className="mt-5 text-sm italic text-white/70">{current.summary}</p>

                  <div className="mt-5 grid gap-2 sm:grid-cols-2">
                    {current.items.map((it, i) => (
                      <div key={i} className="glass flex items-start gap-3 rounded-xl p-4">
                        <ItemIcon status={it.status} />
                        <div>
                          <div className="text-sm font-semibold text-white">{it.label}</div>
                          <div className="mt-0.5 text-xs text-white/55">{it.detail}</div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="mt-8 flex flex-col items-center gap-3 rounded-2xl border border-[#FF512F]/30 bg-gradient-to-r from-[#FF512F]/10 to-[#F09819]/10 p-6 text-center sm:flex-row sm:text-left">
                    <div className="flex-1">
                      <div className="font-display text-lg font-bold">
                        Fix every gap above — automatically.
                      </div>
                      <div className="mt-1 text-sm text-white/65">
                        Soro patches schemas, writes question-style headings, and publishes
                        AI-ready content daily. Set it up in 60 seconds.
                      </div>
                    </div>
                    {cta("checker-cta-button", "Auto-fix with Soro", "checker")}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// ----------------- Keywords -----------------
const Keywords = () => {
  const [copied, setCopied] = useState(null);
  const handleCopy = async (k, i) => {
    try {
      await navigator.clipboard.writeText(k);
      setCopied(i);
      toast.success("Copied to clipboard", { description: k });
      setTimeout(() => setCopied((v) => (v === i ? null : v)), 1600);
    } catch {
      toast.error("Copy failed");
    }
  };
  const copyAll = async () => {
    try {
      await navigator.clipboard.writeText(KEYWORDS.join("\n"));
      toast.success("All 25 keywords copied");
    } catch {
      toast.error("Copy failed");
    }
  };

  return (
    <section id="keywords" className="relative py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-6">
        <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
          <div className="max-w-2xl">
            <Eyebrow>25 ready-to-rank phrases</Eyebrow>
            <h2 className="mt-5 font-display text-3xl font-extrabold tracking-tight sm:text-5xl">
              Steal these <span className="text-gradient-flame">25 keywords.</span>
            </h2>
            <p className="mt-4 text-white/60">
              Click any card to copy it. Drop them straight into Soro and let it write & publish for you.
            </p>
          </div>
          <button
            data-testid="copy-all-keywords"
            onClick={copyAll}
            className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.03] px-5 py-2.5 text-sm font-semibold text-white/85 transition-colors hover:bg-white/10"
          >
            <Copy className="h-4 w-4" /> Copy all 25
          </button>
        </div>

        <div className="mt-12 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {KEYWORDS.map((k, i) => (
            <motion.button
              key={k}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.35, delay: (i % 9) * 0.03 }}
              data-testid={`keyword-card-${i}`}
              onClick={() => handleCopy(k, i)}
              className="group relative flex items-center justify-between gap-3 overflow-hidden rounded-2xl border border-white/10 bg-white/[0.025] px-5 py-4 text-left transition-all hover:-translate-y-0.5 hover:border-[#F09819]/40 hover:bg-white/[0.06]"
            >
              <div className="flex items-center gap-3">
                <span className="font-display text-xs font-extrabold text-white/25">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="text-sm font-medium text-white/90 group-hover:text-white">{k}</span>
              </div>
              <span className="ml-2 grid h-8 w-8 shrink-0 place-items-center rounded-lg border border-white/10 bg-black/40 text-white/40 transition-colors group-hover:border-[#F09819]/40 group-hover:text-[#F09819]">
                {copied === i ? <Check className="h-4 w-4" /> : <Copy className="h-3.5 w-3.5" />}
              </span>
            </motion.button>
          ))}
        </div>
      </div>
    </section>
  );
};

// ----------------- Integrations marquee -----------------
const Integrations = () => {
  const platforms = [
    "WordPress",
    "Shopify",
    "Webflow",
    "Wix",
    "Notion",
    "HubSpot",
    "Ghost",
    "Next.js",
    "Squarespace",
    "Framer",
  ];
  const row = [...platforms, ...platforms];
  return (
    <section className="relative py-20">
      <div className="mx-auto max-w-7xl px-6 text-center">
        <Eyebrow>Works with your stack</Eyebrow>
        <h2 className="mt-5 font-display text-2xl font-extrabold tracking-tight sm:text-4xl">
          Publishes anywhere you already write.
        </h2>
      </div>
      <div className="mt-10 overflow-hidden [mask-image:linear-gradient(to_right,transparent,black_15%,black_85%,transparent)]">
        <div className="flex w-max animate-marquee gap-3">
          {row.map((p, i) => (
            <div
              key={i}
              className="glass flex items-center gap-3 rounded-2xl px-6 py-4 text-sm font-semibold text-white/80"
            >
              <span className="grid h-7 w-7 place-items-center rounded-lg bg-gradient-to-br from-[#FF512F] to-[#F09819] font-display text-[11px] font-black text-white">
                {p[0]}
              </span>
              {p}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ----------------- Comparison -----------------
const ComparisonCell = ({ v, highlight }) => {
  if (typeof v === "boolean")
    return v ? (
      <CheckCircle2 className={`mx-auto h-5 w-5 ${highlight ? "text-[#F09819]" : "text-white/40"}`} />
    ) : (
      <XCircle className="mx-auto h-5 w-5 text-white/15" />
    );
  return <div className={`text-sm font-semibold ${highlight ? "text-white" : "text-white/60"}`}>{v}</div>;
};

const Comparison = () => {
  const rows = [
    { feature: "Works while you sleep", soro: true, agency: false, diy: false },
    { feature: "Ranks on Google + ChatGPT", soro: true, agency: true, diy: false },
    { feature: "Personalized brand voice", soro: true, agency: true, diy: false },
    { feature: "Setup in 60 seconds", soro: true, agency: false, diy: false },
    { feature: "50+ languages", soro: true, agency: false, diy: false },
    { feature: "Monthly cost", soro: "$$", agency: "$$$$$", diy: "Your time" },
  ];
  return (
    <section className="relative py-24 sm:py-32">
      <div className="mx-auto max-w-5xl px-6">
        <div className="max-w-2xl">
          <Eyebrow>Soro vs the alternatives</Eyebrow>
          <h2 className="mt-5 font-display text-3xl font-extrabold tracking-tight sm:text-5xl">
            Save <span className="text-gradient-flame">$5,000/month</span>.
            <br />Skip the busywork.
          </h2>
        </div>

        <div className="glass-strong mt-12 overflow-hidden rounded-3xl">
          <div className="grid grid-cols-4 border-b border-white/5 bg-white/[0.02]">
            <div className="p-5 text-xs font-bold uppercase tracking-[0.2em] text-white/40">Feature</div>
            <div className="relative p-5 text-center">
              <div className="inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-[#FF512F] to-[#F09819] px-3 py-1 font-display text-xs font-extrabold uppercase tracking-wider text-white">
                <Sparkles className="h-3 w-3" /> Soro
              </div>
            </div>
            <div className="p-5 text-center font-display text-sm font-bold text-white/70">Agency</div>
            <div className="p-5 text-center font-display text-sm font-bold text-white/70">DIY</div>
          </div>
          {rows.map((r, i) => (
            <div
              key={r.feature}
              data-testid={`compare-row-${i}`}
              className={`grid grid-cols-4 items-center border-b border-white/5 ${
                i % 2 === 0 ? "bg-white/[0.01]" : ""
              }`}
            >
              <div className="p-5 text-sm text-white/85">{r.feature}</div>
              <div className="bg-gradient-to-b from-[#FF512F]/10 to-transparent p-5 text-center">
                <ComparisonCell v={r.soro} highlight />
              </div>
              <div className="p-5 text-center"><ComparisonCell v={r.agency} /></div>
              <div className="p-5 text-center"><ComparisonCell v={r.diy} /></div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ----------------- Testimonials -----------------
const Testimonials = () => {
  const items = [
    {
      n: "Jude Morrison",
      l: "Solo founder · UK",
      q: "I run everything solo and Soro saved my business. I got more bookings from Google than from paid ads.",
    },
    {
      n: "Amanda Clark",
      l: "Founder · US",
      q: "My organic traffic doubled in just a few weeks. No more spending on ads that don't convert.",
    },
    {
      n: "Malik Jackson",
      l: "SEO consultant · US",
      q: "I've been doing SEO for 10 years and Soro still amazed me. I now use it on every client site.",
    },
    {
      n: "Matt Armstrong",
      l: "DTC operator · US",
      q: "First results came in three weeks — ditched paid ads immediately. Setup was instant.",
    },
    {
      n: "Laura Hayes",
      l: "Shopify store · CA",
      q: "Started using last month and holy cow it is fast. My shop is getting free traffic.",
    },
    {
      n: "Richmond Pak",
      l: "Marketer · AU",
      q: "One of the best SEO tools you can find these days. I stopped working with an agency last year.",
    },
  ];
  return (
    <section className="relative py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-6">
        <div className="max-w-3xl">
          <Eyebrow>1,000+ founders growing daily</Eyebrow>
          <h2 className="mt-5 font-display text-3xl font-extrabold tracking-tight sm:text-5xl">
            Real stories. <span className="text-gradient-flame">Real traffic.</span>
          </h2>
        </div>
        <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((t, i) => (
            <motion.div
              key={t.n}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.45, delay: i * 0.05 }}
              data-testid={`testimonial-${i}`}
              className="glass flex h-full flex-col justify-between rounded-2xl p-7 transition-all hover:-translate-y-1 hover:border-white/20"
            >
              <div>
                <div className="flex">
                  {[0, 1, 2, 3, 4].map((s) => (
                    <Star key={s} className="h-3.5 w-3.5 fill-[#F09819] text-[#F09819]" />
                  ))}
                </div>
                <p className="mt-4 text-sm leading-relaxed text-white/80">&ldquo;{t.q}&rdquo;</p>
              </div>
              <div className="mt-6 flex items-center gap-3 border-t border-white/5 pt-4">
                <div className="grid h-9 w-9 place-items-center rounded-full bg-gradient-to-br from-[#FF512F] to-[#F09819] font-display text-xs font-extrabold text-white">
                  {t.n.split(" ").map((x) => x[0]).join("")}
                </div>
                <div>
                  <div className="text-sm font-semibold">{t.n}</div>
                  <div className="text-xs text-white/45">{t.l}</div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ----------------- FAQ -----------------
const FAQ = () => {
  const [open, setOpen] = useState(0);
  return (
    <section id="faq" className="relative py-24 sm:py-32">
      <div className="mx-auto max-w-3xl px-6">
        <div className="text-center">
          <Eyebrow>Questions, answered</Eyebrow>
          <h2 className="mt-5 font-display text-3xl font-extrabold tracking-tight sm:text-5xl">
            Everything you want to know.
          </h2>
        </div>
        <div className="mt-12 divide-y divide-white/5 rounded-3xl glass overflow-hidden">
          {FAQS.map((f, i) => {
            const isOpen = open === i;
            return (
              <div key={f.q} data-testid={`faq-item-${i}`}>
                <button
                  data-testid={`faq-toggle-${i}`}
                  onClick={() => setOpen(isOpen ? -1 : i)}
                  className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left"
                >
                  <span className="font-display text-base font-bold text-white sm:text-lg">{f.q}</span>
                  <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full border border-white/10 text-[#F09819]">
                    {isOpen ? <Minus className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                  </span>
                </button>
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.25 }}
                      className="overflow-hidden"
                    >
                      <div className="px-6 pb-6 text-sm leading-relaxed text-white/65">{f.a}</div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// ----------------- Final CTA -----------------
const FinalCTA = () => (
  <section className="relative py-28">
    <div className="mx-auto max-w-5xl px-6">
      <div className="relative overflow-hidden rounded-[2rem] border border-white/10 bg-gradient-to-br from-[#FF512F]/20 via-[#F09819]/15 to-[#00F2FE]/20 p-10 sm:p-16">
        <div className="absolute -right-20 -top-20 h-72 w-72 rounded-full bg-[#FF512F] opacity-30 blur-3xl" />
        <div className="absolute -left-20 -bottom-20 h-72 w-72 rounded-full bg-[#F09819] opacity-30 blur-3xl" />
        <div className="relative">
          <Eyebrow>Ready to grow daily?</Eyebrow>
          <h2 className="mt-5 font-display text-3xl font-black leading-tight tracking-tight sm:text-5xl lg:text-6xl">
            Become the answer
            <br />
            <span className="text-gradient-flame">on Google &amp; ChatGPT.</span>
          </h2>
          <p className="mt-5 max-w-xl text-white/70">
            Stop trading hours for traffic. Soro researches, writes and publishes the content that ranks
            — every single day — while you build your business.
          </p>
          <div className="mt-9 flex flex-col items-start gap-4 sm:flex-row sm:items-center">
            {cta("final-cta-button", "Start growing — try Soro", "final")}
            <span className="text-sm text-white/60">No contracts · cancel anytime</span>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// ----------------- Footer -----------------
const Footer = () => (
  <footer className="border-t border-white/5 py-12">
    <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-6 px-6 text-sm text-white/45 sm:flex-row">
      <div className="flex items-center gap-2">
        <div className="grid h-7 w-7 place-items-center rounded-lg bg-gradient-to-br from-[#FF512F] to-[#F09819]">
          <TrendingUp className="h-3.5 w-3.5 text-white" />
        </div>
        <div>
          <div className="font-display font-bold text-white/85">Grow Your Presence Online Daily</div>
          <div className="text-xs">Independent partner of Soro · {new Date().getFullYear()}</div>
        </div>
      </div>
      <div className="flex items-center gap-6">
        <Link to="/" className="hover:text-white" data-testid="footer-home">Home</Link>
        <Link to="/blog" className="hover:text-white" data-testid="footer-blog">Blog</Link>
        <a
          href={AFFILIATE_URL}
          target="_blank"
          rel="sponsored noopener noreferrer"
          onClick={() => trackCta("footer")}
          className="font-semibold text-[#F09819] hover:text-white"
          data-testid="footer-cta-link"
        >
          Try Soro →
        </a>
      </div>
    </div>
    <div className="mx-auto mt-6 max-w-7xl px-6 text-center text-[11px] text-white/30">
      Affiliate disclosure: We may earn a commission when you sign up through links on this page.
    </div>
  </footer>
);

// ----------------- App -----------------
import BlogIndex from "@/blog/BlogIndex";
import BlogPost from "@/blog/BlogPost";
import { resetPageSeo } from "@/blog/seo";

const HomePage = () => {
  const location = useLocation();
  useEffect(() => {
    // Reset any dynamic SEO that a blog page injected so static SEO is used on home.
    resetPageSeo();
    if (location.hash) {
      const id = location.hash.replace("#", "");
      const el = document.getElementById(id);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 80);
    } else {
      window.scrollTo(0, 0);
    }
  }, [location.hash, location.pathname]);

  return (
    <>
      <Hero />
      <HowItWorks />
      <Features />
      <Checker />
      <Integrations />
      <Comparison />
      <Testimonials />
      <FAQ />
      <FinalCTA />
    </>
  );
};

function App() {
  useEffect(() => {
    // ping backend (best-effort)
    axios.get(`${API}/`).catch(() => {});
  }, []);

  return (
    <div className="App relative min-h-screen bg-[#050508] font-sans text-white">
      <Toaster
        position="bottom-center"
        theme="dark"
        toastOptions={{
          style: {
            background: "rgba(15,15,22,0.95)",
            border: "1px solid rgba(255,255,255,0.1)",
            color: "#fff",
          },
        }}
      />
      <Header />
      <main>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/blog" element={<BlogIndex />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
        </Routes>
      </main>
      <Footer />
    </div>
  );
}

export default App;
